import React, { useState, useEffect } from 'react';
import { Box, Container, TextField, Button, Typography, Alert, Tabs, Tab } from '@mui/material';
import api from '../services/api';

const SimpleLogin = ({ onLoginSuccess }) => {
  const [tab, setTab] = useState(0); // 0 = login, 1 = register
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Check URL for ?tab=register
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'register') setTab(1);
  }, []);

  const testAccounts = [
    { email: 'admin@angels67.com', password: 'admin123', name: 'Admin User', role: 'ADMIN' },
    { email: 'investor@angels67.com', password: 'investor123', name: 'Demo Investor', role: 'INVESTOR' },
    { email: 'founder@angels67.com', password: 'founder123', name: 'Demo Founder', role: 'FOUNDER' },
    { email: 'demo@angels67.com', password: 'demo123', name: 'Demo User', role: 'INVESTOR' },
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Please enter email and password');
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/auth/login', { email, password });
      const { token, user } = response.data.data;
      localStorage.setItem('access_token', token);
      localStorage.setItem('demo_user', JSON.stringify(user));
      if (onLoginSuccess) onLoginSuccess(user);
      window.location.href = '/dashboard';
    } catch (err) {
      const msg = err.response?.data?.error || 'Login failed';
      setError(msg + '. Try one of the demo accounts below.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!email || !firstName || !lastName) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      await api.post('/api/auth/register', {
        email,
        name: `${firstName} ${lastName}`,
        firstName,
        lastName,
        phone: phone || undefined,
      });
      setSuccess('Account created! You can now log in.');
      setTab(0);
      setPassword('');
    } catch (err) {
      const msg = err.response?.data?.error || 'Registration failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = (account) => {
    setTab(0);
    setEmail(account.email);
    setPassword(account.password);
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 4,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: 'white',
        }}
      >
        <Typography component="h1" variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Angels67
        </Typography>

        <Tabs value={tab} onChange={(_, v) => { setTab(v); setError(''); setSuccess(''); }} sx={{ mb: 3, width: '100%' }}>
          <Tab label="Sign In" sx={{ flex: 1 }} />
          <Tab label="Register" sx={{ flex: 1 }} />
        </Tabs>

        {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success}</Alert>}

        {tab === 0 ? (
          /* ── LOGIN TAB ── */
          <>
            <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
              <TextField margin="normal" required fullWidth label="Email Address" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
              <TextField margin="normal" required fullWidth label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2, py: 1.5 }} disabled={loading}>
                {loading ? 'Logging in...' : 'Sign In'}
              </Button>
            </Box>

            <Box sx={{ width: '100%', mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                Quick Login (Demo Accounts):
              </Typography>
              {testAccounts.map((account, index) => (
                <Button key={index} fullWidth variant="outlined" size="small" sx={{ mb: 1, textTransform: 'none' }} onClick={() => quickLogin(account)}>
                  {account.name} — {account.role} ({account.email})
                </Button>
              ))}
            </Box>
          </>
        ) : (
          /* ── REGISTER TAB ── */
          <Box component="form" onSubmit={handleRegister} sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField margin="normal" required fullWidth label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus />
              <TextField margin="normal" required fullWidth label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </Box>
            <TextField margin="normal" required fullWidth label="Email Address" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField margin="normal" fullWidth label="Phone (optional)" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2, py: 1.5 }} disabled={loading}>
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mt: 1 }}>
              After registering, you can log in with your email and a demo password.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default SimpleLogin;
